import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import FilterRow from '@components/Common/FilterRow';
import PageHeader from '@components/Common/PageHeader';
import EmptyState from '@components/Common/EmptyState';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import FormField from '@components/Common/FormField';
import MintlyTable from '@components/Common/Table';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './index.scss';
import { formatDateTime } from '@helpers/date';
import StatTile from '@components/Common/StatTile';
import { updateAdminPushEnabledMutation } from '@constants/graphQLQueries';
import {
  type PushPermission,
  requestPushPermission,
  usePushPermission,
} from '@helpers/push/pushPermission';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const NOTIFICATION_COLUMNS = ['user', 'type', 'title', 'userType', 'status', 'createdAt'];

// ── Tab routing ───────────────────────────────────────────────────────────────

const TAB_SLUGS = ['my-alerts', 'user-notifications', 'content-broadcasts'] as const;
type TabSlug = typeof TAB_SLUGS[number];

// ── GraphQL helpers ───────────────────────────────────────────────────────────

const buildGetAdminNotifs = (page: number, size: number) =>
  `{ getAdminNotifications(page: ${page}, size: ${size}) { id type title body link isRead createdAt } }`;

const buildGetPrefs = () =>
  `{ getAdminNotificationPreferences { userId subscribedEvents availableEvents } }`;

/*
 * The Chrome notifications switch, read on its own rather than added to buildGetPrefs. If the
 * console ships ahead of the backend field, a combined query fails validation as a whole and takes
 * the event switches down with it; apart, only this row degrades (to "on", the server's default).
 */
const GET_PUSH_ENABLED = `{ getAdminNotificationPreferences { pushEnabled } }`;

const buildUpdatePrefs = (events: string[]) =>
  `mutation { updateAdminNotificationPreferences(subscribedEvents: [${events.map((e) => `"${e}"`).join(', ')}]) { userId subscribedEvents availableEvents } }`;

const buildGetStats = (userType: string) =>
  `{ getNotificationStats(userType: "${userType}") { total pending seen byType { type count } } }`;

const buildGetMgmtNotifs = (userType: string, status: string, page: number, size: number) =>
  `{ getNotificationsForManagement(userType: "${userType}", status: "${status}", page: ${page}, size: ${size}) { content { id userId userType userEmail userName type title body isRead createdAt } totalElements totalPages pageNumber } }`;

const buildTriggerSingle = (userId: string, userType: string, type: string, title: string, body: string, link: string) =>
  `mutation { triggerNotification(userId: "${userId}", userType: "${userType}", type: "${type}", title: "${escapeGql(title)}", body: "${escapeGql(body)}", link: "${escapeGql(link)}") { id title } }`;

const buildTriggerBulk = (userType: string, type: string, title: string, body: string, link: string) =>
  `mutation { triggerBulkNotification(userType: "${userType}", type: "${type}", title: "${escapeGql(title)}", body: "${escapeGql(body)}", link: "${escapeGql(link)}") { created skipped } }`;

function escapeGql(s: string) {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function fmtTime(iso?: string) {
  return formatDateTime(iso);
}

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * One label per AdminNotificationEventType. The switch list is built from the server's
 * availableEvents, so a missing entry does not hide a row: the fallback is `?? ev`, and
 * the screen renders the raw constant. PREMIUM_HIRING_REQUEST and ANALYTICS_ALERT were
 * both shown that way. BLOG_POST_PUBLISHED and RESOURCE_GUIDE_PUBLISHED used to be here
 * and are gone from the enum: nothing ever fired them, so they were switches that
 * controlled nothing.
 *
 * notificationLabels.test.ts pins this map against the Java enum, because it has now
 * drifted from it twice.
 */
const ADMIN_EVENT_LABELS: Record<string, string> = {
  NEW_EMPLOYER_SIGNUP: 'New Employer Signup',
  NEW_JOBSEEKER_SIGNUP: 'New Job Seeker Signup',
  NEW_JOB_POSTED: 'New Job Posted',
  NEW_JOB_APPLICATION: 'New Job Application',
  NEW_COMPANY_REQUEST: 'New Company Request',
  NEW_SUBSCRIPTION: 'New Subscription',
  PREMIUM_HIRING_REQUEST: 'Premium Hiring Request',
  ANALYTICS_ALERT: 'Weekly Analytics Digest',
  FOLLOW_UP_DUE: 'Follow-ups due',
};

const NOTIF_TYPE_OPTIONS = [
  { value: 'NEW_BLOG_POST',              label: 'New Blog Post' },
  { value: 'NEW_GUIDE',                  label: 'New Resource Guide' },
  { value: 'NEW_JOB_POSTED',            label: 'New Job Posted' },
  { value: 'APPLICATION_STATUS_CHANGED', label: 'Application Status Changed' },
  { value: 'NEW_APPLICATION_RECEIVED',   label: 'New Application Received' },
  { value: 'PROFILE_COMPLETE_NUDGE',     label: 'Profile Complete Nudge' },
  { value: 'RESUME_UPLOAD_NUDGE',        label: 'Resume Upload Nudge' },
  { value: 'COMPANY_PROFILE_NUDGE',      label: 'Company Profile Nudge' },
];

const TARGET_OPTIONS = [
  { value: 'both',      label: 'Everyone (Employers + Job Seekers)' },
  { value: 'employer',  label: 'Employers only' },
  { value: 'jobseeker', label: 'Job Seekers only' },
];

const USER_TYPE_OPTIONS_WITH_BOTH = [
  { value: 'employer',  label: 'Employer' },
  { value: 'jobseeker', label: 'Job Seeker' },
  { value: 'both',      label: 'Both' },
];

const USER_TYPE_OPTIONS = USER_TYPE_OPTIONS_WITH_BOTH.slice(0, 2);

// ── Tab 1: My Alerts ──────────────────────────────────────────────────────────

/** What the row says about Chrome's permission, and the status chip that goes with it. */
const PUSH_STATUS: Record<PushPermission, { chip: string; label: string; hint: string }> = {
  granted: { chip: 'active', label: 'On', hint: 'Alerts you get here also appear as Chrome notifications on this computer.' },
  denied: {
    chip: 'suspended',
    label: 'Blocked in Chrome',
    hint: 'To unblock: click the icon left of the address bar, open Site settings, and set Notifications to Allow.',
  },
  default: { chip: 'pending', label: 'Not set', hint: 'Get a Chrome notification for each alert you subscribe to below, even with this tab in the background.' },
  unsupported: { chip: 'inactive', label: 'Not supported', hint: 'This browser cannot show Chrome notifications. Use Chrome on a desktop.' },
};

/**
 * The Chrome notifications row (owner, 2026-10-24). Two separate things on one line, because they
 * answer two separate questions:
 *   - Chrome's permission, per browser: the chip, and "Turn on" while Chrome has no answer;
 *   - this person's switch, per account (`pushEnabled`): whether the notification service sends
 *     them pushes at all, on any browser they have allowed.
 * The switch saves on its own, immediately, and sends only `pushEnabled`, so it never commits the
 * event switches' unsaved edits below.
 */
const ChromeNotificationsRow: React.FC = () => {
  const { showToast } = useToast();
  const permission = usePushPermission();
  const [pushEnabled, setPushEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGraphQLApiData(GET_PUSH_ENABLED)
      .then((res) => {
        // null means "never set", which the server treats as on.
        const value = res?.data?.getAdminNotificationPreferences?.pushEnabled;
        setPushEnabled(value !== false);
      })
      .catch(() => {});
  }, []);

  const togglePush = async (next: boolean) => {
    const previous = pushEnabled;
    setPushEnabled(next);
    setSaving(true);
    try {
      const res = await fetchGraphQLApiData(updateAdminPushEnabledMutation(next));
      const saved = res?.data?.updateAdminNotificationPreferences?.pushEnabled;
      setPushEnabled(saved == null ? next : saved !== false);
      showToast(next ? 'Chrome notifications on' : 'Chrome notifications off', 'success');
    } catch {
      setPushEnabled(previous);
      showToast('Failed to save Chrome notifications', 'error');
    } finally {
      setSaving(false);
    }
  };

  const turnOn = async () => {
    const answer = await requestPushPermission();
    if (answer === 'denied') showToast('Chrome blocked notifications for this site', 'warning');
  };

  const status = PUSH_STATUS[permission];

  return (
    <Box className="notif-push-row" data-testid="chrome-notifications-row">
      <Box className="notif-push-text">
        <Box className="notif-push-heading">
          <Typography variant="body2" className="notif-pref-label" id="chrome-notifications-label">
            Chrome notifications
          </Typography>
          <CRMStatusChip status={status.chip} label={status.label} />
        </Box>
        <Typography className="notif-push-hint">{status.hint}</Typography>
      </Box>
      <Box className="notif-push-controls">
        {permission === 'default' && (
          <Button variant="outlined" size="small" onClick={turnOn}>
            Turn on
          </Button>
        )}
        <Switch
          checked={pushEnabled}
          disabled={saving}
          onChange={(_e, checked) => togglePush(checked)}
          slotProps={{ input: { 'aria-labelledby': 'chrome-notifications-label' } }}
        />
      </Box>
    </Box>
  );
};


const MyAlertsTab: React.FC = () => {
  const { showToast } = useToast();
  const [notifs, setNotifs] = useState<any[]>([]);
  const [prefs, setPrefs] = useState<{ subscribedEvents: string[]; availableEvents: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [localSubscribed, setLocalSubscribed] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchGraphQLApiData(buildGetAdminNotifs(0, 20)).catch(() => null),
      fetchGraphQLApiData(buildGetPrefs()).catch(() => null),
    ]).then(([nRes, pRes]) => {
      setNotifs(nRes?.data?.getAdminNotifications ?? []);
      const p = pRes?.data?.getAdminNotificationPreferences;
      if (p) {
        setPrefs(p);
        setLocalSubscribed(p.subscribedEvents ?? []);
      }
    }).finally(() => setLoading(false));
  }, []);

  const toggleEvent = (ev: string) => {
    setLocalSubscribed((prev) =>
      prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev],
    );
  };

  // What the server last told us, so a failed save can put the switches back.
  const savedEvents = prefs?.subscribedEvents ?? [];
  const isDirty =
    [...localSubscribed].sort().join(',') !== [...savedEvents].sort().join(',');

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const res = await fetchGraphQLApiData(buildUpdatePrefs(localSubscribed));
      // Adopt what came back rather than assuming the request was echoed. The server
      // now filters unknown event names, so the saved list is not always the sent one.
      const saved = res?.data?.updateAdminNotificationPreferences;
      if (saved) {
        setPrefs(saved);
        setLocalSubscribed(saved.subscribedEvents ?? []);
      } else {
        setPrefs((prev: any) =>
          prev ? { ...prev, subscribedEvents: localSubscribed } : prev,
        );
      }
      showToast('Preferences saved', 'success');
    } catch {
      // Put the switches back to what the server holds. Leaving the optimistic value
      // showing told the user the save failed while the screen carried on displaying
      // the state they wanted, so the next thing they saw was a page that disagreed
      // with itself on reload.
      setLocalSubscribed(savedEvents);
      showToast('Failed to save preferences', 'error');
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) return <SkeletonLoader rows={8} />;

  return (
    <Box className="notif-my-alerts">
      <Box className="notif-feed-section">
        <Typography variant="h6" className="notif-section-title">Recent Alerts</Typography>
        {notifs.length === 0 ? (
          <EmptyState
            icon={<NotificationsNoneOutlinedIcon />}
            title="No notifications yet"
            body="Alerts appear here when platform events occur: new signups, job applications, subscriptions."
          />
        ) : (
          /* One surface with hairlines between rows, like every other CC list. Each alert used to be
             its own tinted, bordered card, so twenty of them read as twenty things shouting; unread
             is a dot and a heavier title now, not a fill. */
          <ul className="notif-feed-list">
            {notifs.map((n: any) => (
              <li key={n.id} className={`notif-feed-item${!n.isRead ? ' notif-feed-item--unread' : ''}`}>
                <span className="notif-feed-dot" aria-hidden="true" />
                <span className="notif-feed-text">
                  <span className="notif-feed-title">
                    {n.title}
                    {!n.isRead && <span className="sr-only"> (unread)</span>}
                  </span>
                  {n.body && <span className="notif-feed-body">{n.body}</span>}
                </span>
                <time className="notif-feed-time" dateTime={n.createdAt}>{fmtTime(n.createdAt)}</time>
              </li>
            ))}
          </ul>
        )}
      </Box>

      <Box className="notif-prefs-section">
        <Typography variant="h6" className="notif-section-title">Notification Preferences</Typography>

        {/*
          * Chrome notifications are real now (owner, 2026-10-24): a service worker receives
          * pushes from the notification service for the same alerts the bell shows, filtered by
          * the switches below. This row used to be a button that changed a chip and delivered
          * nothing, and was removed for that; it is back because the delivery behind it exists.
          */}
        <ChromeNotificationsRow />


        {(prefs?.availableEvents ?? []).length === 0 && (
          <EmptyState compact title="No preferences available" />
        )}

        {(prefs?.availableEvents ?? []).map((ev: string) => (
          <Box key={ev} className="notif-pref-row">
            <Typography variant="body2" className="notif-pref-label">
              {ADMIN_EVENT_LABELS[ev] ?? ev}
            </Typography>
            {/* No size="small": theme.ts now gives every switch one size, so this asked for a
                variant it could not get and was the only one in the console to ask. */}
            <Switch
              checked={localSubscribed.includes(ev)}
              onChange={() => toggleEvent(ev)}
            />
          </Box>
        ))}

        <Button
          variant="contained"
          size="small"
          onClick={savePrefs}
          // Also gated on there being a change. The switches move instantly, so an
          // always-enabled Save button gave no way to tell a pending edit from a saved
          // one.
          disabled={savingPrefs || !isDirty}
          sx={{ mt: 2 }}
        >
          {savingPrefs ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  );
};

// ── Tab 2: User Notifications ─────────────────────────────────────────────────

const UserNotificationsTab: React.FC = () => {
  const { showToast } = useToast();
  const [userType, setUserType] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [mgmtData, setMgmtData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerMode, setTriggerMode] = useState<'single' | 'bulk'>('single');
  const [triggerUserId, setTriggerUserId] = useState('');
  const [triggerUserType, setTriggerUserType] = useState('employer');
  const [triggerType, setTriggerType] = useState('NEW_BLOG_POST');
  const [triggerTitle, setTriggerTitle] = useState('');
  const [triggerBody, setTriggerBody] = useState('');
  const [triggerLink, setTriggerLink] = useState('/');
  const [triggerResult, setTriggerResult] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([
        fetchGraphQLApiData(buildGetStats(userType)).catch(() => null),
        fetchGraphQLApiData(buildGetMgmtNotifs(userType, status, page, 25)).catch(() => null),
      ]);
      setStats(sRes?.data?.getNotificationStats);
      setMgmtData(mRes?.data?.getNotificationsForManagement);
    } finally {
      setLoading(false);
    }
  }, [userType, status, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTrigger = async () => {
    // The body carries an asterisk and nothing enforced it: the button guards the title and the
    // recipient only, so a notification could go out with an empty message.
    if (!triggerBody.trim()) {
      showToast('A notification body is required', 'error');
      document.getElementById('body')?.focus();
      return;
    }
    try {
      let msg = '';
      if (triggerMode === 'single') {
        await fetchGraphQLApiData(buildTriggerSingle(triggerUserId, triggerUserType, triggerType, triggerTitle, triggerBody, triggerLink));
        msg = 'Notification sent';
      } else {
        const res = await fetchGraphQLApiData(buildTriggerBulk(triggerUserType, triggerType, triggerTitle, triggerBody, triggerLink));
        const r = res?.data?.triggerBulkNotification;
        msg = `Created ${r?.created ?? 0} notification(s)`;
      }
      setTriggerResult(msg);
      setTriggerOpen(false);
      showToast(msg, 'success');
      fetchData();
    } catch (e: any) {
      const errMsg = 'Error: ' + (e?.message ?? 'Failed');
      setTriggerResult(errMsg);
      showToast(errMsg, 'error');
    }
  };

  const rows: any[] = mgmtData?.content ?? [];

  return (
    <Box className="notif-mgmt-tab">
      {/* Stats row */}
      <Box className="cc-stat-grid">
        <StatTile label="Total" value={stats?.total ?? '-'} />
        <StatTile label="Pending" value={stats?.pending ?? '-'} />
        <StatTile label="Seen" value={stats?.seen ?? '-'} />
      </Box>

      {/* One shared filter row. The gap, alignment and control widths come from FilterRow;
          only the trailing action's push-right is this page's. */}
      <FilterRow className="notif-filters-row">
        <SelectFormField
          required={false}
          name="userType"
          label="User Type"
          size="small"
          options={[
            { value: 'all', label: 'All' },
            { value: 'employer', label: 'Employers' },
            { value: 'jobseeker', label: 'Job Seekers' },
          ]}
          value={userType}
          onChange={(_n, v) => { setUserType(String(v)); setPage(0); }}
        />
        <SelectFormField
          required={false}
          name="status"
          label="Status"
          size="small"
          options={[
            { value: 'all', label: 'All' },
            { value: 'pending', label: 'Pending' },
            { value: 'seen', label: 'Seen' },
          ]}
          value={status}
          onChange={(_n, v) => { setStatus(String(v)); setPage(0); }}
        />
        <Button variant="outlined" size="small" onClick={fetchData}>Refresh</Button>
        <Button
          className="notif-trailing-action"
          variant="contained"
          size="small"
          onClick={() => setTriggerOpen(true)}
        >
          Trigger Notification
        </Button>
      </FilterRow>

      {/* Table */}
      {loading ? (
        <SkeletonLoader rows={10} columns={NOTIFICATION_COLUMNS.length} />
      ) : (
        <MintlyTable
          rows={rows}
          selectors={NOTIFICATION_COLUMNS}
          tableHeadRow={{
            user: 'User',
            type: 'Type',
            title: 'Title',
            userType: 'User Type',
            status: 'Status',
            createdAt: 'Created',
          }}
          tableBodyRow={{
            user: (row) => (
              <Box>
                <Typography variant="body2" className="notif-user-name">
                  {row.userName || '-'}
                </Typography>
                <Typography variant="caption" className="notif-user-email">
                  {row.userEmail || '-'}
                </Typography>
              </Box>
            ),
            type: (row) => <Chip label={row.type} size="small" variant="outlined" />,
            title: (row) => row.title,
            userType: (row) => row.userType,
            status: (row) => (
              <CRMStatusChip
                status={row.isRead ? 'inactive' : 'pending'}
                label={row.isRead ? 'Seen' : 'Pending'}
              />
            ),
            createdAt: (row) => <span className="notif-date-cell">{fmtTime(row.createdAt)}</span>,
          }}
          pagination={{
            enabled: mgmtData?.totalPages > 1,
            page,
            rowsPerPage: 25,
            totalNumberOfRows: mgmtData?.totalElements ?? 0,
            handleChangePage: (_e, newPage) => setPage(newPage),
          }}
        />
      )}

      {/* Trigger modal */}
      <Dialog open={triggerOpen} onClose={() => setTriggerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger Notification</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant={triggerMode === 'single' ? 'contained' : 'outlined'} onClick={() => setTriggerMode('single')}>
              Single User
            </Button>
            <Button size="small" variant={triggerMode === 'bulk' ? 'contained' : 'outlined'} onClick={() => setTriggerMode('bulk')}>
              Bulk
            </Button>
          </Box>

          {triggerMode === 'single' && (
            <FormField
              name="userId"
              label="User ID"
              value={triggerUserId}
              onChange={(_n, v) => setTriggerUserId(v)}
              size="small"
              required
            />
          )}

          <SelectFormField
            required={false}
            name="userType"
            label="User Type"
            options={triggerMode === 'bulk' ? USER_TYPE_OPTIONS_WITH_BOTH : USER_TYPE_OPTIONS}
            value={triggerUserType}
            onChange={(_n, v) => setTriggerUserType(String(v))}
            size="small"
          />

          <SelectFormField
            required={false}
            name="notifType"
            label="Notification Type"
            options={NOTIF_TYPE_OPTIONS}
            value={triggerType}
            onChange={(_n, v) => setTriggerType(String(v))}
            size="small"
          />

          <FormField
            name="title"
            label="Title"
            value={triggerTitle}
            onChange={(_n, v) => setTriggerTitle(v)}
            size="small"
            required
          />

          <FormField
            required
            name="body"
            label="Body"
            value={triggerBody}
            onChange={(_n, v) => setTriggerBody(v)}
            size="small"
            multiline
            rows={2}
          />

          <FormField
            required={false}
            name="link"
            label="Link"
            value={triggerLink}
            onChange={(_n, v) => setTriggerLink(v)}
            size="small"
          />

          {triggerResult && <Alert severity="info">{triggerResult}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTrigger}
            disabled={!triggerTitle || (triggerMode === 'single' && !triggerUserId)}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Tab 3: Content Broadcasts ─────────────────────────────────────────────────

const ContentBroadcastsTab: React.FC = () => {
  const [notifType, setNotifType] = useState('NEW_BLOG_POST');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [target, setTarget] = useState('both');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');

  const handleSend = async () => {
    if (!title) return;
    if (!body.trim()) {
      // This component surfaces outcomes through `result`, not the toast, so the failure has to
      // appear where the successes already do.
      setResult('A body is required');
      document.getElementById('body')?.focus();
      return;
    }
    setSending(true);
    setResult('');
    try {
      const res = await fetchGraphQLApiData(buildTriggerBulk(target, notifType, title, body, link));
      const r = res?.data?.triggerBulkNotification;
      setResult(`Sent to ${r?.created ?? 0} user(s)${r?.skipped ? `, ${r.skipped} skipped` : ''}`);
    } catch (e: any) {
      setResult('Error: ' + (e?.message ?? 'Failed'));
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setTitle('');
    setBody('');
    setLink('');
    setTarget('both');
    setNotifType('NEW_BLOG_POST');
    setResult('');
  };

  return (
    <Box className="notif-content-tab">
      <Paper className="notif-broadcast-card" elevation={0}>
        <Typography variant="h6" className="notif-section-title">Broadcast Notification</Typography>
        <Typography variant="body2" className="notif-broadcast-desc">
          Send a push notification to all opted-in users. Choose the notification type, compose the message, and select your target audience.
        </Typography>

        <Box className="notif-broadcast-form">
          <SelectFormField
            name="notifType"
            label="Notification Type"
            options={NOTIF_TYPE_OPTIONS}
            value={notifType}
            onChange={(_n, v) => setNotifType(String(v))}
            required
          />

          <FormField
            name="title"
            label="Title"
            value={title}
            onChange={(_n, v) => setTitle(v)}
            required
            placeholder="e.g. New blog post: Top 10 hiring tips"
          />

          <FormField
            required
            name="body"
            label="Body / Excerpt"
            value={body}
            onChange={(_n, v) => setBody(v)}
            multiline
            rows={3}
            placeholder="Short summary shown in the push notification..."
          />

          <FormField
            required={false}
            name="link"
            label="Link (URL or path)"
            value={link}
            onChange={(_n, v) => setLink(v)}
            placeholder="/resources/hiring-tips or https://blog.trymintly.com/..."
          />

          <SelectFormField
            name="target"
            label="Target Audience"
            options={TARGET_OPTIONS}
            value={target}
            onChange={(_n, v) => setTarget(String(v))}
            required
          />

          <Box className="notif-broadcast-actions">
            <Button variant="outlined" size="small" onClick={handleReset} disabled={sending}>
              Reset
            </Button>
            {/* `small`, matching Reset beside it. These two sit in one action row and were 30px
                and 35px — the console has exactly two button sizes and a row may only use one.
                Compact is the in-card size, and this row is the footer of a card. */}
            <Button
              variant="contained"
              size="small"
              disabled={sending || !title}
              onClick={handleSend}
            >
              {sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Send Notification'}
            </Button>
          </Box>

          {result && (
            <Alert severity={result.startsWith('Error') ? 'error' : 'success'} sx={{ mt: 1 }}>
              {result}
            </Alert>
          )}
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Paper className="notif-broadcast-card" elevation={0}>
        <Typography variant="h6" className="notif-section-title">Daily Automated Broadcasts</Typography>
        <Typography variant="body2" className="notif-broadcast-desc">
          The notification service runs a daily scan at 9 AM IST to detect newly published blog posts
          and resource guides, then automatically fans out push + email notifications to all opted-in users.
          Enable via <code>CONTENT_NOTIFICATIONS_ENABLED=true</code> on the notification service.
        </Typography>
      </Paper>
    </Box>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

/** One sentence per tab, the header's subtitle, so the page says what this tab is for. */
const TAB_HERO = [
  {
    kicker: 'My alerts',
    title: 'What the platform has been doing while you were away.',
    text: 'Signups, applications and subscription events, newest first. Choose which of them reach you.',
  },
  {
    kicker: 'User notifications',
    title: 'Send a notification to job seekers or employers.',
    text: 'Targeted by audience and delivered in-app. Nothing here sends an email.',
  },
  {
    kicker: 'Content broadcasts',
    title: 'Announce a guide, a feature or a campaign to everyone at once.',
    text: 'One message, every user in the chosen audience, delivered in-app.',
  },
];

const NotificationsPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const rawTab = tab as TabSlug | undefined;
  const tabIndex = rawTab && TAB_SLUGS.includes(rawTab) ? TAB_SLUGS.indexOf(rawTab) : 0;

  // Redirect bare /notifications to canonical first tab path
  useEffect(() => {
    if (!rawTab || !TAB_SLUGS.includes(rawTab)) {
      navigate(`/notifications/${TAB_SLUGS[0]}`, { replace: true });
    }
  }, [rawTab, navigate]);

  const handleTabChange = (v: number) => {
    navigate(`/notifications/${TAB_SLUGS[v]}`, { replace: true });
  };

  const hero = TAB_HERO[tabIndex] ?? TAB_HERO[0];

  return (
    <Box className="notif-root">
      {/* H1 first, the active tab's sentence under it, then the tabs. It used to open on the tab
          bar, with the sentence as an H2 in a hero card below it. */}
      <PageHeader
        title="Notifications"
        icon={<NotificationsOutlinedIcon />}
        subtitle={(
          <>
            {hero.title}
            <span className="cc-page-header__subtitle-more"> {hero.text}</span>
          </>
        )}
        tabs={{
          value: tabIndex,
          onChange: handleTabChange,
          tabs: [
            { label: 'My Alerts', icon: <NotificationsOutlinedIcon fontSize="small" /> },
            { label: 'User Notifications', icon: <SendOutlinedIcon fontSize="small" /> },
            { label: 'Content Broadcasts', icon: <CampaignOutlinedIcon fontSize="small" /> },
          ],
        }}
      />

      <Box className="notif-tab-content">
        {tabIndex === 0 && <MyAlertsTab />}
        {tabIndex === 1 && <UserNotificationsTab />}
        {tabIndex === 2 && <ContentBroadcastsTab />}
      </Box>
    </Box>
  );
};

export default NotificationsPage;
